import {useEffect, useMemo, useState} from "react"
import {Col, Row, Tag} from "antd"
import moment from "moment-timezone"
import {useQuery} from "@tanstack/react-query"
import {isEmpty} from "lodash"
import {useDebounce} from "react-use"
import {handleError} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useSearchParam} from "hooks"
import {useFilterStorage} from "hooks/useFilterStorage"
import {attendanceService} from "services"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import {BaseRangePicker} from "components/DateTimePicker"
import {KlassDropdown} from "components/inputs"
import CampusSelect from "components/CampusSelect"
import TermSelect from "components/TermSelect"
import ProgramSelect from "components/ProgramSelect"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import UserSelectV3 from "components/UserSelectV3"
import PhaseSelect from "components/PhaseSelect"
import ShiftSelect from "components/ShiftSelect"
import GroupSelect from "components/GroupSelect"
import {Auth} from "types/auth"
import {AttendanceChangeRequest, AttendanceChangeRequestStatus} from "types/attendance"
import {FilterKey} from "types/filter"
import {DailyAttendanceDetailsPopup} from "../DailyAttendanceDetailsPopup"
import {CHANGE_REQUEST_STATUS_COLOR, ChangeRequestReviewPopup} from "./ChangeRequestReviewPopup"
import styles from "./DailyAttendanceChangeRequests.module.css"

type Props = {
    studentProfileId?: number
    [key: string]: any
}

const COLUMNS = [
    {title: "Date", field: "dateHtml", headerStyle: {minWidth: "150px"}, columnIndex: 1},
    {title: "Student Id", field: "customProfileId", headerStyle: {minWidth: "120px"}, columnIndex: 2},
    {title: "Student Name", field: "studentName", headerStyle: {minWidth: "150px"}, columnIndex: 3},
    {title: "Reason", field: "reason", headerStyle: {minWidth: "250px"}, columnIndex: 4},
    {title: "Requested At", field: "createdAtHtml", headerStyle: {minWidth: "170px"}, columnIndex: 5},
    {title: "Status", field: "statusHtml", headerStyle: {minWidth: "110px"}, columnIndex: 6},
    {title: "Reviewed By", field: "reviewedByName", headerStyle: {minWidth: "150px"}, columnIndex: 7}
]

const STATUS_OPTIONS = Object.values(AttendanceChangeRequestStatus).map((value) => ({id: value, name: value}))

const initialDefaultFilter = {
    statuses: [{id: AttendanceChangeRequestStatus.Pending, name: AttendanceChangeRequestStatus.Pending}],
    campuses: [],
    startingTerms: [],
    programs: [],
    programVersions: [],
    currentTerms: [],
    studentStatuses: [],
    instructors: [],
    phases: [],
    shifts: [],
    groups: []
}

const toIds = (items: any[] | undefined, key: string) => (isEmpty(items) ? undefined : items.map((item) => item[key]))

const getFilterMemoryKey = () => FilterKey.DailyAttendanceChangeRequests
const getColumns = () => COLUMNS
const getFields = () => COLUMNS.map((column) => column.title)

function DailyAttendanceChangeRequestsView(props: Props) {
    const {studentProfileId, dispatch, page, pageSize, total, data, isLoading, isLoadedTableFuncs} = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const dateTimeFormat = model.getUserDateTimeFormat()

    const defaultFilter = useMemo(
        () => ({...initialDefaultFilter, campuses: model.getCurrentUserProfile()?.campuses ?? []}),
        [model]
    )
    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        FilterKey.DailyAttendanceChangeRequests,
        defaultFilter
    )
    const [filter, setFilter] = useState(currentFilter)
    const onChangeFilter = (key: string, value: any) => setCurrentFilter({...currentFilter, [key]: value})
    const onApplyFilter = () => {
        applyFilter()
        setFilter(currentFilter)
    }
    const selectedCampusIds = (currentFilter.campuses ?? []).map((campus) => campus.id).filter(Boolean)
    const allUserCampusIds = useMemo(
        () => (model.user?.profiles ?? []).flatMap((profile) => (profile.campuses ?? []).map(({id}) => id)),
        [model.user]
    )
    const [searchDebounce, setSearchDebounce] = useState(searchValue)
    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const [dateRange, setDateRange] = useState<string[]>([])
    const [selected, setSelected] = useState<AttendanceChangeRequest>()
    const [attendanceRequest, setAttendanceRequest] = useState<AttendanceChangeRequest>()
    const {value: requestId, onChange: onChangeRequestId} = useSearchParam("requestId", {
        parse: (value) => Number(value) || null
    })

    useEffect(() => {
        dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: () => {}},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const normalizeItem = (request: AttendanceChangeRequest) => ({
        ...request,
        customProfileId: request.student?.customProfileId ?? "",
        reviewedByName: request.reviewedByName ?? "",
        dateHtml: <span>{moment(request.attendanceDate).format(dateFormat)}</span>,
        createdAtHtml: <span>{moment(request.createdAt).format(dateTimeFormat)}</span>,
        statusHtml: <Tag color={CHANGE_REQUEST_STATUS_COLOR[request.status]}>{request.status}</Tag>
    })

    useQuery(
        ["attendance-change-requests", studentProfileId, filter, dateRange, searchDebounce, page, pageSize],
        async () => {
            dispatch({isLoading: true})
            const {data: requests, total: count} = await attendanceService.listAttendanceChangeRequests({
                status: toIds(filter.statuses, "id"),
                studentProfileIds: studentProfileId ? [studentProfileId] : undefined,
                campusIds: toIds(filter.campuses, "id"),
                startingTermIds: toIds(filter.startingTerms, "id"),
                programIds: toIds(filter.programs, "id"),
                majorVersionIds: toIds(filter.programVersions, "id"),
                currentTermIds: toIds(filter.currentTerms, "id"),
                statusIds: toIds(filter.studentStatuses, "statusId"),
                instructorProfileIds: toIds(filter.instructors, "profileId"),
                phaseIds: toIds(filter.phases, "phaseId"),
                shiftIds: toIds(filter.shifts, "shiftId"),
                groupIds: toIds(filter.groups, "id"),
                fromDate: dateRange[0],
                toDate: dateRange[1],
                search: searchDebounce || undefined,
                page,
                pageSize
            })
            return {data: requests.map(normalizeItem), total: count}
        },
        {
            enabled: !!isLoadedTableFuncs,
            onSuccess: ({data: rows, total: count}) => {
                dispatch({data: rows, total: count, isLoading: false})
            },
            onError: (err) => {
                dispatch({isLoading: false})
                handleError(err)
            }
        }
    )

    const {data: linkedRequest} = useQuery(
        ["attendance-change-requests", "linked", requestId],
        async () => {
            const {data: requests} = await attendanceService.listAttendanceChangeRequests({ids: [requestId]})
            return requests[0]
        },
        {
            enabled: !!requestId,
            onError: (err) => {
                handleError(err)
            }
        }
    )
    const reviewing = selected ?? (requestId ? linkedRequest : undefined)
    const closeReview = () => {
        setSelected(undefined)
        if (requestId) {
            onChangeRequestId(null)
        }
    }
    const viewAttendance = (request: AttendanceChangeRequest) => {
        setAttendanceRequest(request)
        closeReview()
    }

    return (
        <div style={{paddingBottom: 150}}>
            <BaseNewFilter
                searchValue={searchValue}
                onSearchInput={changeSearch}
                onClick={onApplyFilter}
                onClickClear={clearFilter}
                filter={currentFilter}
                savedFilterOptions={{
                    enabled: true,
                    filterKey: FilterKey.DailyAttendanceChangeRequests,
                    setFilter: setCurrentFilter,
                    defaultFilter
                }}
                renderRightFilter={() => (
                    <div className={styles.rightFilter}>
                        <BaseRangePicker
                            placeholder={["Date From", "Date To"]}
                            value={dateRange.length ? [moment(dateRange[0]), moment(dateRange[1])] : null}
                            onChange={(newValue) => {
                                setDateRange(
                                    newValue ? [newValue[0].format("YYYY-MM-DD"), newValue[1].format("YYYY-MM-DD")] : []
                                )
                                props.onChangePage(1)
                            }}
                        />
                    </div>
                )}>
                <Row gutter={[40, 32]}>
                    <Col span={12}>
                        <KlassDropdown
                            isMulti
                            isClearable
                            placeholder="Request Status"
                            options={STATUS_OPTIONS}
                            valueKey="id"
                            labelKey="name"
                            value={currentFilter.statuses}
                            onChange={(newValue) => onChangeFilter("statuses", newValue ?? [])}
                        />
                    </Col>
                    <Col span={12}>
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder="Campus"
                            displayOnlyCampusIds={allUserCampusIds}
                            value={currentFilter.campuses}
                            onChange={(newValue) =>
                                setCurrentFilter({...currentFilter, campuses: newValue, currentTerms: []})
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <TermSelect
                            isMulti
                            isClearable
                            placeholder="Starting Terms"
                            value={currentFilter.startingTerms}
                            onChange={(newValue) => onChangeFilter("startingTerms", newValue)}
                            campusIds={selectedCampusIds.length ? selectedCampusIds : undefined}
                        />
                    </Col>
                    <Col span={12}>
                        <ProgramSelect
                            isMulti
                            isClearable
                            placeholder="Programs"
                            value={currentFilter.programs}
                            onChange={(newValue) => onChangeFilter("programs", newValue)}
                            campusId={selectedCampusIds.length ? selectedCampusIds : undefined}
                        />
                    </Col>
                    <Col span={12}>
                        <ProgramVersionSelect
                            isMulti
                            isClearable
                            programId={currentFilter.programs?.map((item) => item.id)}
                            placeholder="Program Versions"
                            value={currentFilter.programVersions}
                            onChange={(newValue) => onChangeFilter("programVersions", newValue)}
                            campusIds={selectedCampusIds.length ? selectedCampusIds : undefined}
                        />
                    </Col>
                    <Col span={12}>
                        <TermSelect
                            isMulti
                            isClearable
                            placeholder="Current Terms"
                            value={currentFilter.currentTerms}
                            onChange={(newValue) => onChangeFilter("currentTerms", newValue ?? null)}
                            campusIds={selectedCampusIds.length ? selectedCampusIds : undefined}
                        />
                    </Col>
                    <Col span={12}>
                        <NewStudentStatusSelectFilter
                            isMulti
                            isClearable
                            value={currentFilter.studentStatuses}
                            onChange={(newValue) => onChangeFilter("studentStatuses", newValue)}
                        />
                    </Col>
                    <Col span={12}>
                        <UserSelectV3
                            isMulti
                            isClearable
                            type={[Auth.UserProfileType.Staff]}
                            value={currentFilter.instructors}
                            belongsToMyCampuses={false}
                            onChange={(newValue) => onChangeFilter("instructors", newValue)}
                            placeholder="Instructors"
                        />
                    </Col>
                    <Col span={12}>
                        <PhaseSelect
                            isMulti
                            isClearable
                            placeholder="Phases"
                            value={currentFilter.phases}
                            onChange={(newValue) => onChangeFilter("phases", newValue ?? null)}
                        />
                    </Col>
                    <Col span={12}>
                        <ShiftSelect
                            isMulti
                            isClearable
                            placeholder="Shifts"
                            value={currentFilter.shifts}
                            onChange={(newValue) => onChangeFilter("shifts", newValue ?? null)}
                        />
                    </Col>
                    <Col span={12}>
                        <GroupSelect
                            isMulti
                            isClearable
                            placeholder="Groups"
                            value={currentFilter.groups}
                            onChange={(newValue) => onChangeFilter("groups", newValue ?? null)}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={props.fields}
                allFields={props.allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={props.columns}
                data={data}
                isLoading={isLoading}
                fields={props.fields}
                orderField={props.orderField}
                allFields={props.allFields}
                onClickRowItem={setSelected}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
            <ChangeRequestReviewPopup
                request={reviewing}
                timezone={reviewing?.student?.timezone ?? moment.tz.guess()}
                onClose={closeReview}
                onViewAttendance={viewAttendance}
            />
            <DailyAttendanceDetailsPopup
                isShow={!!attendanceRequest}
                onClose={() => setAttendanceRequest(undefined)}
                selectedAttendance={
                    attendanceRequest && {
                        profileId: attendanceRequest.studentProfileId,
                        date: attendanceRequest.attendanceDate,
                        attendances: [{id: attendanceRequest.attendanceId}],
                        student: attendanceRequest.student
                    }
                }
            />
        </div>
    )
}

export const DailyAttendanceChangeRequests = KlassappTableHOC(DailyAttendanceChangeRequestsView)
